import { useState, useEffect, useRef } from 'react';

import { Card, Form, Input, Button, message, Table, Tag, Modal, Upload, Space, Popconfirm, Select, Switch, Slider } from 'antd';

import { PlusOutlined, UploadOutlined, EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, AuditOutlined, SearchOutlined, QrcodeOutlined, DownloadOutlined } from '@ant-design/icons';

import axios from 'axios';

import { getUser } from '../../api';

import { sealCanvas, COMPANY_STYLES, PERSONAL_STYLES } from '../../utils/sealCanvas';

import QRCode from '../../components/QRCode';

import PersonalSealModal from '../../components/PersonalSealModal';
import SignerConfirmModal from './SignerConfirmModal';

const API = import.meta.env.VITE_API_BASE || '/api';

const token = () => localStorage.getItem('ar_token');

const h = () => ({ Authorization: 'Bearer ' + token() });

// 印章图片接口：<img> 无法携带 header，通过 query 参数携带 token

const sealImgUrl = (id) => `/api/customer/signer/${id}/seal-image?token=${encodeURIComponent(token() || '')}`;

// 入库章（CompanySeal）图片接口

const companySealImgUrl = (id) => `/api/asign/stamp/image/${id}?token=${encodeURIComponent(token() || '')}`;

export default function CustomerAuth() {

  const user = getUser();

  const isAdmin = user?.role === 3;

  const isBusiness = user?.role === 2;

  const isCustomer = user?.role === 1;

  const [authLetters, setAuthLetters] = useState([]);

  const [signers, setSigners] = useState([]);

  const [authModalOpen, setAuthModalOpen] = useState(false);

  const [signerModalOpen, setSignerModalOpen] = useState(false);

  const [pwdQrModal, setPwdQrModal] = useState({ open: false, signer: null }); // 签收密码二维码弹窗

  const [signerConfirmOpen, setSignerConfirmOpen] = useState(false); // 签收人确认弹窗

  const [pendingSigners, setPendingSigners] = useState([]); // [{name,phone,id_card,signature_base64,key}]

  const [pendingLetterId, setPendingLetterId] = useState(null);


  const [authForm] = Form.useForm();

  const [signerForm] = Form.useForm();
  const [modifySignerModalOpen, setModifySignerModalOpen] = useState(false);
  const [modifyingSigner, setModifyingSigner] = useState(null);
  const [modifySignerForm] = Form.useForm();

  const [authOk, setAuthOk] = useState(false);

  const [enterpriseOk, setEnterpriseOk] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  const [reviewModal, setReviewModal] = useState({ open: false, letter: null, action: '' });

  const [certModalOpen, setCertModalOpen] = useState(false);

  const [certForm] = Form.useForm();

  const [certLoading, setCertLoading] = useState(false);

  const [tutorialOpen, setTutorialOpen] = useState(false);

  const [companyInfo, setCompanyInfo] = useState({ company_name: '', credit_code: '' });

  const [adminCustomer, setAdminCustomer] = useState(null);

  const [customerOptions, setCustomerOptions] = useState([]);

  const [serialNoModal, setSerialNoModal] = useState(false);

  const [serialNoForm] = Form.useForm();

  // 委托书筛选

  const [letterFilter, setLetterFilter] = useState({ name: '', code: '', status: '' });

  // 签收人筛选

  const [signerFilter, setSignerFilter] = useState({ code: '', opStatus: '' });

  // 印章管理搜索

  const [stampSearch, setStampSearch] = useState('');

  // 个人印章制作弹窗（新爱签V2参数化弹窗）

  const [personalSealModal, setPersonalSealModal] = useState({ open: false, signer: null });

  // 印章管理列表

  const [stampAdminList, setStampAdminList] = useState([]);

  // ========== 提交审核状态 ==========

  const [readiness, setReadiness] = useState(null); // { ready, checks, missing, submit_status, signer_count }

  const [readinessLoading, setReadinessLoading] = useState(false);

  const [submitLoading, setSubmitLoading] = useState(false);

  const [stampAdminLoading, setStampAdminLoading] = useState(false);

  const fetchStampAdminList = () => {

    if (!isAdmin) return;

    setStampAdminLoading(true);

    axios.get('/api/asign/stamp/admin/list', { headers: h() })

      .then(r => setStampAdminList(r.data.data || []))

      .catch(() => {})

      .finally(() => setStampAdminLoading(false));

  };

  const fetchAll = () => {

    axios.get(API + '/customer/auth-letter', { headers: h() }).then(r => setAuthLetters(r.data.data || []));

    axios.get(API + '/customer/signers', { headers: h() }).then(r => setSigners(r.data.data || []));

    axios.get(API + '/customer/auth-status', { headers: h() }).then(r => {

      setAuthOk(r.data?.auth_status === 'passed');

      setEnterpriseOk(r.data?.enterprise_ok || r.data?.enterprise_status === 'passed');

    }).catch(() => {});

    if (isAdmin) fetchStampAdminList();

    fetchMySealList();

    fetchReadiness();

  };

  useEffect(() => {

    fetchAll();

  }, [isAdmin]);

  // ========== 提交审核 ==========

  const fetchReadiness = () => {

    if (isAdmin || isBusiness) return;

    setReadinessLoading(true);

    axios.get(API + '/customer/readiness-check', { headers: h() })

      .then(r => { if (r.data?.success) setReadiness(r.data.data); })

      .catch(() => {})

      .finally(() => setReadinessLoading(false));

  };

  const handleSubmitReview = async () => {

    setSubmitLoading(true);

    try {

      const r = await axios.post(API + '/customer/submit-review', {}, { headers: h() });

      if (r.data.success) {

        message.success('提交审核成功，请等待管理员审核！');

        fetchAll();

      } else {

        // 有未完成的项

        const missing = r.data.missing || [];

        message.warning(r.data.message || '资料未完善');

        if (r.data.checks) setReadiness({ ...readiness, checks: r.data.checks, missing });

      }

    } catch (e) {

      const msg = e.response?.data?.message || e.response?.data?.detail || '提交失败';

      message.error(msg);

    }

    setSubmitLoading(false);

  };

  useEffect(() => {

    fetchAll();

    axios.get(API + '/customer/company-info', { headers: h() }).then(r => {

      if (r.data.success) setCompanyInfo({ company_name: r.data.company_name || '', credit_code: r.data.credit_code || '' });

    }).catch(() => {});

    if (isAdmin) {

      axios.get(API + '/dashboard/filters', { headers: h() }).then(r => {

        if (r.data?.customers) setCustomerOptions(r.data.customers.map(c => ({ label: c, value: c })));

      }).catch(() => {});

    }

  }, [isAdmin]);

  const statusMap = {

    pending: { color: 'default', text: '待提交' },

    submitted: { color: 'warning', text: '待审核' },

    passed: { color: 'success', text: '已通过' },

    rejected: { color: 'error', text: '已驳回' },

  };

  // ========== 管理员审核 ==========

  const handleReview = async () => {

    const { letter, action } = reviewModal;

    try {

      await axios.post(API + `/admin/auth-letter/${letter.id}/review`, {

        status: action, reason: '',

      }, { headers: h() });

      message.success(action === 'passed' ? '已通过' : '已驳回');

      setReviewModal({ open: false, letter: null, action: '' });

      fetchAll();

    } catch { message.error('操作失败'); }

  };

  // ========== 企业认证 ==========

  const handleCompanyCert = async () => {

    try {

      const vals = await certForm.validateFields();

      setCertLoading(true);

      const r = await axios.post(API + '/customer/company-identify', {

        ...vals, cCusCode: isAdmin ? adminCustomer : undefined,

      }, { headers: h() });

      if (r.data.success && r.data.identify_url) {

        setCertModalOpen(false);

        certForm.resetFields();

        const certWindow = window.open(r.data.identify_url, 'aisign_cert');

        const newSerialNo = r.data.serial_no || '';

        message.info('认证页面已打开，完成后将自动检测并关闭');

        // 轮询检测认证状态，完成后自动关闭认证窗口
        let pollCount = 0;
        const pollInterval = setInterval(async () => {
          pollCount++;
          if (pollCount > 120) { clearInterval(pollInterval); return; } // 最多轮询10分钟
          // 检测窗口是否已被用户手动关闭
          if (certWindow && certWindow.closed) { clearInterval(pollInterval); return; }
          try {
            const cr = await axios.post(API + '/customer/company-cert-done', {
              serial_no: newSerialNo,
              cCusCode: isAdmin ? adminCustomer : undefined,
            }, { headers: h() });
            if (cr.data.success && cr.data.passed) {
              clearInterval(pollInterval);
              message.success('企业认证已通过！认证窗口已自动关闭');
              try { certWindow && certWindow.close(); } catch {}
              fetchAll();
            }
          } catch { /* 忽略轮询异常 */ }
        }, 5000);

        fetchAll();

      } else {

        message.error(r.data.message || '获取认证链接失败');

      }

    } catch (e) {

      if (e.errorFields) return;

      const msg = e.response?.data?.detail || e.response?.data?.message || '请求失败';

      if (typeof msg === 'string') message.error(msg); else message.error('获取认证链接失败');

    }

    setCertLoading(false);

  };

  // ========== 委托书上传 ==========

  const handleAuthSubmit = async () => {

    try {

      const vals = await authForm.validateFields();

      setSubmitting(true);

      const fd = new FormData();

      if (vals.file?.fileList?.[0]?.originFileObj) {

        fd.append('file', vals.file.fileList[0].originFileObj);

      }

      const r = await axios.post(API + '/customer/auth-letter', fd, { headers: { ...h(), 'Content-Type': 'multipart/form-data' } });

      message.success('提交成功');

      setAuthModalOpen(false);

      fetchAll();

    } catch (e) {

      if (e.response?.data?.detail) message.error(e.response.data.detail);

      else if (e.errorFields) return;

      else message.error('提交失败');

    }

    setSubmitting(false);

  };

  // ========== 签收人管理 ==========

  const handleSignerSubmit = async () => {

    try {

      const vals = await signerForm.validateFields();

      // Find passed auth letter/enterprise cert

      // Use submitted or passed auth letter

      const letter = authLetters.find(l => l.auth_status === 'submitted' || l.auth_status === 'passed');

      if (!letter) { message.error('请先上传委托书'); return; }

      const fd = new FormData();

      fd.append('auth_letter_id', letter.id);

      fd.append('signer_name', vals.signer_name);

      fd.append('phone', vals.phone);

      fd.append('id_card', vals.id_card || '');

      if (vals.sign_password) {
        fd.append('sign_password', vals.sign_password);
        fd.append('confirm_password', vals.confirm_password || '');
      }

      await axios.post(API + '/customer/signer', fd, { headers: { ...h(), 'Content-Type': 'multipart/form-data' } });

      message.success('添加成功');

      setSignerModalOpen(false);

      fetchAll();

    } catch (e) {

      if (e.response?.data?.detail) message.error(e.response.data.detail);

      else if (e.errorFields) return;

      else message.error('添加失败');

    }

  };

  // ========== 签收人确认（批量保存） ==========

  const handleSignerFieldChange = (key, field, value) => {

    setPendingSigners(prev => prev.map(s => s.key === key ? { ...s, [field]: value } : s));

  };

  const addSignerRow = () => {

    setPendingSigners(prev => [...prev, { name: '', phone: '', id_card: '', signature_base64: '', key: `s${Date.now()}` }]);

  };

  const removeSignerRow = (key) => {

    setPendingSigners(prev => prev.filter(s => s.key !== key));

  };

  const handleSignatureFile = (key, file) => {

    const reader = new FileReader();

    reader.onload = () => {

      const base64 = reader.result.split(',')[1]; // remove data:image/...;base64, prefix

      setPendingSigners(prev => prev.map(s => s.key === key ? { ...s, signature_base64: base64 } : s));

    };

    reader.readAsDataURL(file);

    return false; // prevent auto upload

  };

  const batchSaveSigners = async () => {

    if (pendingSigners.length === 0) { message.error('至少需要一个签收人'); return; }

    for (let i = 0; i < pendingSigners.length; i++) {

      const s = pendingSigners[i];

      if (!s.name || !s.phone) { message.error(`第${i + 1}个签收人姓名和电话不能为空`); return; }

      if (!s.signature_base64) { message.error(`请上传第${i + 1}个签收人（${s.name}）的签名图片`); return; }

    }

    try {

      await axios.post(API + '/customer/signers/batch-save', {

        letter_id: pendingLetterId,

        signers: pendingSigners.map(s => ({ name: s.name, phone: s.phone, id_card: s.id_card, signature_base64: s.signature_base64 })),

      }, { headers: h() });

      message.success(`已保存 ${pendingSigners.length} 位签收人，等待管理员审核`);

      setSignerConfirmOpen(false);

      fetchAll();

    } catch (e) {

      message.error(e.response?.data?.detail || '保存失败');

    }

  };

  const toggleLetter = async (id, currentActive) => {

    await axios.put(API + `/customer/auth-letter/${id}/toggle`, {}, { headers: h() });

    message.success(currentActive ? '已停用' : '已启用');

    fetchAll();

  };

  const openModifySigner = (signer) => {
    setModifyingSigner(signer);
    modifySignerForm.setFieldsValue({
      signer_name: signer.signer_name,
      phone: signer.phone,
      id_card: signer.id_card,
    });
    setModifySignerModalOpen(true);
  };

  const handleModifySignerSubmit = async () => {
    try {
      const vals = await modifySignerForm.validateFields();
      const fd = new FormData();
      fd.append('signer_name', vals.signer_name);
      fd.append('phone', vals.phone);
      fd.append('id_card', vals.id_card);
      await axios.put(`${API}/customer/signer/${modifyingSigner.id}/modify`, fd, { headers: h() });
      message.success('修改申请已提交，等待管理员审核');
      setModifySignerModalOpen(false);
      fetchAll();
    } catch (e) {
      if (e.response?.data?.detail) message.error(e.response.data.detail);
      else if (e.errorFields) return;
      else message.error('修改失败');
    }
  };

  const toggleSigner = async (id, currentActive) => {

    await axios.put(API + `/customer/signer/${id}/toggle`, {}, { headers: h() });

    message.success(currentActive ? '已停用' : '已启用');

    fetchAll();

  };

  const deleteLetter = async (id) => {

    Modal.confirm({

      title: '确认删除？',

      content: '删除后将无法恢复',

      okText: '确认删除',

      okButtonProps: { danger: true },

      onOk: async () => {

        await axios.delete(API + `/customer/auth-letter/${id}`, { headers: h() });

        message.success('已删除');

        fetchAll();

      },

    });

  };

  const deleteSigner = async (id, name) => {

    Modal.confirm({

      title: `确认删除签收人 ${name}？`,

      content: '删除后将同时清空关联印章',

      okText: '确认删除',

      okButtonProps: { danger: true },

      onOk: async () => {

        await axios.delete(API + `/customer/signer/${id}`, { headers: h() });

        message.success(`已删除签收人 ${name}`);

        fetchAll();

      },

    });

  };

  // ========== 签收人认证 ==========

  const [authSignerModal, setAuthSignerModal] = useState({ open: false, signer: null, step: 'choose', serialNo: '' });

  // ========== 印章制作状态 ==========

  const [stampModal, setStampModal] = useState(false);

  const [stampFields, setStampFields] = useState({

    type: 'company',

    style: 'oval',

    stampText: '入库专用章',

    topText: '',

    bottomText: '',

    centerText: '入库专用章',

    showStar: true,

    centerFontSize: 0,

    topFontSize: 0,

    bottomFontSize: 0,

  });

  const [stampPreviewSize, setStampPreviewSize] = useState({ w: 200, h: 136 });

  const [stampLoading, setStampLoading] = useState(false);

  const [mySealList, setMySealList] = useState([]);

  const [mySealLoading, setMySealLoading] = useState(false);

  const [editingSealId, setEditingSealId] = useState(null);

  const stampCanvasRef = useRef(null);

  const fetchMySealList = async () => {

    setMySealLoading(true);

    try {

      const res = await axios.get('/api/customer/seals', { headers: h() });

      setMySealList(res.data.data || []);

    } catch (e) {

      console.error('获取我的印章列表失败', e);

    } finally {

      setMySealLoading(false);

    }

  };

  const [stampList, setStampList] = useState([]);

  const [stampCustomerId, setStampCustomerId] = useState(null);

  const updateStampStyle = (style) => {

    const type = stampFields.type;

    setStampPreviewSize(type === 'company'

      ? (style === 'oval' ? { w: 200, h: 136 } : { w: 200, h: 200 })

      : (style === 'oval' ? { w: 160, h: 109 } : { w: 160, h: 160 })

    );

    setStampFields(f => ({

      ...f,

      style,

      ...(style === 'oval' ? { bottomText: '' } : {}),

    }));

  };

  const fetchStampList = async (customerId) => {

    if (!customerId) return;

    try {

      const res = await axios.get(`/api/asign/stamp/list/${customerId}`, { headers: h() });

      setStampList(res.data.data || []);

    } catch (e) {

      console.error('获取印章列表失败', e);

    }

  };

const openCompanySealModal = (existingSeal = null) => {

    if (existingSeal) {

      // 修改模式：预填已有印章数据

      setEditingSealId(existingSeal.id);

      setStampFields({

        type: 'company',

        style: 'oval',

        stampText: existingSeal.seal_title || existingSeal.seal_name || '',

        topText: companyInfo.company_name || user?.ccusname || '',

        bottomText: '',

        centerText: '入库专用章',

        showStar: true,

        centerFontSize: 0,

        topFontSize: 0,

        bottomFontSize: 0,

      });

    } else {

      // 新建模式

      setEditingSealId(null);

      setStampFields({

        type: 'company',

        style: 'oval',

        stampText: (companyInfo.company_name || user?.ccusname || '') + '入库专用章',

        topText: companyInfo.company_name || user?.ccusname || '',

        bottomText: '',

        centerText: '入库专用章',

        showStar: true,

        centerFontSize: 0,

        topFontSize: 0,

        bottomFontSize: 0,

      });

    }

    setStampPreviewSize({ w: 200, h: 136 });

    setStampList([]);

    setStampModal(true);

  };

  const makeCompanySeal = async () => {

    if (!stampFields.topText) return message.error('请填写顶部文字');

    if (!stampFields.centerText) return message.error('请填写中心文字');

    setStampLoading(true);

    try {

      // 从 Canvas 预览捕获 PNG base64

      const canvas = stampCanvasRef.current;

      let image_base64 = '';

      if (canvas) {

        const dataUrl = canvas.toDataURL('image/png');

        image_base64 = dataUrl.replace(/^data:image\/png;base64,/, '');

      } else {

        message.error('Canvas 未就绪，请重试');

        setStampLoading(false);

        return;

      }

      const payload = {

        image_base64,

        stamp_text: stampFields.centerText,    // 中心文字

        top_text: stampFields.topText,          // 顶部文字

        bottom_text: stampFields.bottomText,

        seal_title: stampFields.stampText,       // 印章名称（章的名字）

        style: stampFields.style,

        show_star: stampFields.showStar,

        aged: stampFields.aged ?? false,

        top_font_size: stampFields.topFontSize || 0,

        center_font_size: stampFields.centerFontSize || 0,

        bottom_font_size: stampFields.bottomFontSize || 0,

        seal_id: editingSealId || null,         // 编辑时传已有ID

      };

      const res = await axios.post('/api/customer/upload-stamp-image', payload, { headers: h() });

      if (res.data.success) {

        message.success(res.data.message || '入库章已保存');

        setStampModal(false);

        setEditingSealId(null);

        fetchAll();

        fetchMySealList();

        if (stampCustomerId) fetchStampList(stampCustomerId);

        if (isAdmin) fetchStampAdminList();

      } else {

        message.error(res.data.message || '操作失败');

      }

    } catch (e) {

      message.error('操作失败: ' + (e.response?.data?.message || e.message));

    } finally {

      setStampLoading(false);

    }

  };

  const [verifyForm] = Form.useForm();

  const startSignerAuth = async (signer, mode) => {

    try {

      let url = `${API}/customer/signer/${signer.id}/asign-auth-start?auth_type=${mode}`;

      const r = await axios.post(url, {}, { headers: h() });

      if (r.data.success) {

        if (r.data.step === 'verify_code') {

          setAuthSignerModal({ open: true, signer, step: 'verify', serialNo: r.data.serial_no });

          verifyForm.resetFields();

        } else if (r.data.step === 'web') {

          setAuthSignerModal({ open: false, signer: null, step: 'choose', serialNo: '' });

          const certWindow = window.open(r.data.data?.identifyUrl, 'aisign_signer_cert');

          message.info('认证页面已打开，完成后将自动检测并关闭');

          // 轮询检测认证状态
          let pollCount = 0;
          const pollInterval = setInterval(async () => {
            pollCount++;
            if (pollCount > 120) { clearInterval(pollInterval); return; }
            if (certWindow && certWindow.closed) { clearInterval(pollInterval); return; }
            try {
              const cr = await axios.post(`${API}/customer/signer/${signer.id}/confirm-auth`, {}, { headers: h() });
              if (cr.data.success) {
                clearInterval(pollInterval);
                message.success('签收人认证已完成！认证窗口已自动关闭');
                try { certWindow && certWindow.close(); } catch {}
                fetchAll();
              }
            } catch { /* 忽略轮询异常 */ }
          }, 5000);

        } else if (r.data.step === 'done') {

          message.info('该签收人已完成认证');

          fetchAll();

        } else {

          message.warning(r.data.message || '未知状态');

        }

      } else {

        message.error(r.data.message || '发起认证失败');

      }

    } catch (e) { message.error(e.response?.data?.detail || '认证失败'); }

  };

  const confirmAuthComplete = async (signerId) => {
    message.loading({ content: '正在确认认证状态...', key: 'authConfirm' });
    try {
      const r = await axios.post(`${API}/customer/signer/${signerId}/confirm-auth`, {}, { headers: h() });
      if (r.data.success) {
        message.success({ content: r.data.message || '认证已确认', key: 'authConfirm' });
        fetchAll();
      } else {
        message.error({ content: r.data.message || '认证尚未完成，请在爱签网页完成认证后再试', key: 'authConfirm' });
      }
    } catch {
      message.error({ content: '确认请求失败', key: 'authConfirm' });
    }
  };

  const verifySignerAuth = async () => {

    try {

      const vals = await verifyForm.validateFields();

      const r = await axios.post(`${API}/customer/signer/${authSignerModal.signer.id}/asign-auth-verify`, vals, { headers: h() });

      if (r.data.success) {

        message.success('实名认证完成！');

        setAuthSignerModal({ open: false, signer: null, step: 'choose', serialNo: '' });

        fetchAll();

      } else {

        message.error(r.data.message || '验证失败');

      }

    } catch (e) {

      if (e.errorFields) return;

      message.error(e.response?.data?.detail || '验证失败');

    }

  };

  const uploadSignature = async (signerId) => {

    const input = document.createElement('input');

    input.type = 'file';

    input.accept = 'image/*';

    input.onchange = async (e) => {

      const file = e.target.files[0];

      if (!file) return;

      const fd = new FormData();

      fd.append('file', file);

      try {

        const r = await axios.post(`${API}/customer/signer/${signerId}/upload-signature`, fd, { headers: { ...h(), 'Content-Type': 'multipart/form-data' } });

        if (r.data.success) message.success('签名上传成功');

        fetchAll();

      } catch (e) { message.error(e.response?.data?.detail || '上传失败'); }

    };

    input.click();

  };

  // ========== 附件预览 ==========

  const previewImg = (path) => {

    if (!path) return message.info('暂无文件');

    const url = API.replace('/api', '') + '/' + path.replace(/\\/g, '/');
    const isPDF = path.toLowerCase().endsWith('.pdf');
    const isEmbed = sessionStorage.getItem('embed_mode') === '1';

    // 嵌入模式下，新标签页打开PDF，绕过Edge嵌套iframe限制
    if (isEmbed && isPDF) {
      window.open(url, '_blank');
      return;
    }

    Modal.info({

      title: '预览',

      width: 800,

      content: isPDF

        ? <iframe src={url} style={{ width: '100%', height: '70vh', border: 'none' }} title="PDF预览" />

        : <img src={url} style={{ maxWidth: '100%' }} alt="预览" />,

    });

  };

  const showPreview = (text, record) => (

    <Button type="link" size="small" icon={<EyeOutlined />}

      onClick={() => previewImg(record.letter_img)}>预览</Button>

  );

  return (

    <div style={{ overflowX: 'auto' }}>
      {/* ========== 提交审核（仅客户可见） ========== */}

      {isCustomer && (

        <Card

          title={(() => {
            const isRejected = authLetters.some(l => !l.is_enterprise && l.auth_status === 'rejected');
            const isPassed = authLetters.some(l => !l.is_enterprise && l.auth_status === 'passed');
            const isSubmitted = !isRejected && !isPassed && readiness?.submit_status === 'submitted';
            return (

            <span>

              提交审核

              {isRejected && <Tag color="error" style={{ marginLeft: 8 }}>已驳回</Tag>}

              {isPassed && <Tag color="success" style={{ marginLeft: 8 }}>审核通过</Tag>}

              {isSubmitted && <Tag color="warning" style={{ marginLeft: 8 }}>已提交，请等待审核</Tag>}

              {!isRejected && !isPassed && readiness?.submit_status === 'not_submitted' && readiness?.ready && (

                <Tag color="green" style={{ marginLeft: 8 }}>可提交</Tag>

              )}

              {!isRejected && !isPassed && readiness?.submit_status === 'not_submitted' && !readiness?.ready && (

                <Tag color="default" style={{ marginLeft: 8 }}>待完善</Tag>

              )}

            </span>

            );

          })()}

          style={{ marginBottom: 16 }}

        >

          {readiness?.checks && (

            <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>

              {/* 整体状态图标 */}

              <img

                src={(() => {
                  const isRejected2 = authLetters.some(l => !l.is_enterprise && l.auth_status === 'rejected');
                  const isPassed2 = authLetters.some(l => !l.is_enterprise && l.auth_status === 'passed');
                  const isSubmitted2 = !isRejected2 && !isPassed2 && readiness?.submit_status === 'submitted';
                  if (isPassed2) return '/已审核.png';
                  if (isSubmitted2) return '/待审核.png';
                  return '/待提交.png';
                })()}
                alt="状态"
                style={{ width: 100, height: 100, cursor: 'pointer', flexShrink: 0 }}
                onClick={() => {
                  const el = document.getElementById('section-enterprise');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              />

              {/* 连接线 + 四个检查项横向排列 */}
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', height: 100, gap: 60, padding: '0 30px' }}>

                {/* 背景连接线 — 从企业认证到签收人员 */}
                <div style={{
                  position: 'absolute',
                  top: 'calc(50% - 8px)', left: '30px',
                  width: 'calc(4 * 80px + 3 * 60px + 3ch)',
                  height: 6, marginTop: -3,
                  backgroundColor: '#d9d9d9', borderRadius: 3
                }} />
                <span style={{ position: 'absolute', left: 'calc(30px + 4 * 80px + 3 * 60px + 3ch - 167px)', top: 'calc(50% - 24px)', color: '#d9d9d9', fontSize: 108, lineHeight: '6px' }}>→</span>

                {readiness.checks.map((c, i) => {
                  const sectionId =
                    c.item === 'enterprise' ? 'section-enterprise' :
                    c.item === 'seal' ? 'section-seal' :
                    c.item === 'letter' ? 'section-letter' : 'section-signers';
                  const handleClick = () => {
                    const el = document.getElementById(sectionId);
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  };
                  return (
                    <div
                      key={c.item}
                      onClick={handleClick}
                      style={{
                        position: 'relative', zIndex: 1,
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        cursor: 'pointer', minWidth: 80, padding: '4px 8px',
                        borderRadius: 8, transition: 'background 0.2s',
                        alignSelf: 'flex-start'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <img
                        src={c.ok ? '/完成.png' : '/感叹.png'}
                        alt={c.ok ? '完成' : '未完成'}
                        style={{ width: 70, height: 70, marginBottom: 4 }}
                      />
                      <span style={{ color: c.ok ? '#52c41a' : '#ff4d4f', fontSize: 12, fontWeight: 500, minHeight: 36, display: 'flex', alignItems: 'center', textAlign: 'center' }}>
                        {c.label}
                      </span>
                      <span style={{ color: '#999', fontSize: 10, minHeight: 14 }}>
                        {c.detail || ''}
                      </span>
                    </div>
                  );
                })}
                <Button type="primary" size="large" onClick={handleSubmitReview} loading={submitLoading} style={{ marginLeft: 12, flexShrink: 0, alignSelf: 'flex-start', marginTop: 20 }}>提交审核</Button>

              </div>

            </div>

          )}

          {/* 提交审核按钮已移到流程条 */}
          <div style={{ display: 'none' }}>
            <Button type="primary" size="large" onClick={handleSubmitReview} loading={submitLoading}>提交审核</Button>
          </div>
          {readiness?.ready && readiness?.submit_status === 'not_submitted' && (

            <div style={{ marginTop: 12, color: '#52c41a', fontSize: 13 }}>

              所有资料已完善，点击流程条上「提交审核」即可提交给管理员审核。

            </div>

          )}

          {readiness?.missing?.length > 0 && readiness?.submit_status === 'not_submitted' && (

            <div style={{ marginTop: 12, color: '#ff4d4f', fontSize: 13 }}>

              还缺少：{readiness.missing.join('、')}，请先完善以上资料。

            </div>

          )}

          {!authLetters.some(l => !l.is_enterprise && l.auth_status === 'rejected')

            && !authLetters.some(l => !l.is_enterprise && l.auth_status === 'passed')

            && readiness?.submit_status === 'submitted' && (

            <div style={{ marginTop: 12, color: '#faad14', fontSize: 13 }}>

              已提交审核，请等待管理员审核。审核通过后将自动获得签收权限。

            </div>

          )}

          {authLetters.some(l => !l.is_enterprise && l.auth_status === 'passed') && (

            <div style={{ marginTop: 12, color: '#52c41a', fontSize: 13 }}>

              审核已通过，可以进行到货签收。

            </div>

          )}

          {authLetters.some(l => l.auth_status === 'rejected') && (

            <div style={{ marginTop: 12, color: '#ff4d4f', fontSize: 13 }}>

              {(() => {

                const rej = authLetters.find(l => !l.is_enterprise && l.auth_status === 'rejected');

                return rej?.reject_reason

                  ? <>驳回原因：{rej.reject_reason}。请修改后重新提交！</>

                  : '已驳回，请修改后重新提交！';

              })()}

            </div>

          )}

        </Card>

      )}


      {/* ========== 企业认证入口 ========== */}

      <Card id="section-enterprise" style={{ marginBottom: 16 }}>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>

            {isAdmin && (

              <Select showSearch placeholder="选择客户" value={adminCustomer} onChange={setAdminCustomer}

                style={{ width: 220 }} filterOption={(input, option) => option.label.includes(input)}

                options={customerOptions} allowClear />

            )}

            <Button type="primary" size="large"

              onClick={() => {

                if (isAdmin) {

                  certForm.setFieldsValue({ company_name: '', credit_code: '' });

                } else {

                  certForm.setFieldsValue(companyInfo);

                }

                setCertModalOpen(true);

              }}>企业认证</Button>

            <Button style={{ marginLeft: '4ch' }}

              onClick={async () => {

                // Try to auto-query with saved serial_no first

                setCertLoading(true);

                try {

                  const r = await axios.post(API + '/customer/company-cert-done', {

                    serial_no: '',

                    cCusCode: isAdmin ? adminCustomer : undefined,

                  }, { headers: h() });

                  if (r.data.success && r.data.passed) {

                    message.success('企业认证已通过！');

                    fetchAll();

                  } else {

                    // Show modal, pre-fill serial_no if backend returned it

                    serialNoForm.setFieldsValue({ serial_no: r.data.serial_no || '' });

                    setSerialNoModal(true);

                  }

                } catch {

                  serialNoForm.setFieldsValue({ serial_no: '' });

                  setSerialNoModal(true);

                }

                setCertLoading(false);

              }}>我已完成认证</Button>

            {!isAdmin && enterpriseOk && <Tag color="green">企业已认证通过</Tag>}

            <Button type="primary" size="small" style={{marginLeft: 8}}

              onClick={() => openCompanySealModal(mySealList[0] || null)}>

              {mySealList.length > 0 ? '修改入库章' : '入库章制作'}

            </Button>

          </div>

          {!enterpriseOk && (
            <Popconfirm title="确定要清除当前企业认证记录吗？" onConfirm={async () => {
              try {
                await axios.post(API + '/customer/enterprise-clear', {}, { headers: h() });
                message.success('企业认证记录已清除');
                fetchAll();
              } catch (e) { message.error(e.response?.data?.detail || '清除失败'); }
            }} okText="确定" cancelText="取消">
              <Button danger size="small">清除认证记录</Button>
            </Popconfirm>
          )}
          <Button onClick={() => setTutorialOpen(true)}>查看认证教程</Button>

        </div>

      </Card>

      {/* ========== 印章管理列表 ========== */}

      {isAdmin && (

        <Card

          title="印章管理"
          id="section-seal"

          style={{ marginBottom: 16 }}

          extra={

            <Space>

              <Input

                placeholder="客户名称"

                value={stampSearch}

                onChange={e => setStampSearch(e.target.value)}

                style={{ width: 160 }}

                allowClear

                prefix={<SearchOutlined />}

              />

            </Space>

          }

        >

          <Table

            dataSource={stampAdminList.filter(s =>

              !stampSearch || (s.customer_name || '').includes(stampSearch) ||

              (s.stamp_serial_no || '').includes(stampSearch) ||

              (s.seal_name || '').includes(stampSearch) ||

              (s.seal_title || '').includes(stampSearch)

            )}

            rowKey="id"

            size="small"

            loading={stampAdminLoading}

            pagination={{ pageSize: 10 }}

            columns={[

              {

                title: '印章编号',

                dataIndex: 'asign_seal_no',

                width: 200,

                ellipsis: true,

                render: v => v ? <Tag color="purple" style={{fontFamily:'monospace',fontSize:11}}>{v}</Tag> : <Tag color="default">—</Tag>,

              },

              { title: '客户名称', dataIndex: 'customer_name', width: 160, ellipsis: true },

              {

                title: '印章状态',

                width: 80,

                render: (_, r) => r.seal_img

                  ? <Tag color="green">已制作</Tag>

                  : <Tag color="default">未制作</Tag>,

              },

              {

                title: '爱签同步',

                width: 80,

                render: v => v

                  ? <Tag color="blue">已同步</Tag>

                  : <Tag color="default">未同步</Tag>,

              },

              {

                title: '启用',

                dataIndex: 'is_active',

                width: 70,

                render: v => v === 0

                  ? <Tag color="red">已停用</Tag>

                  : <Tag color="green">已启用</Tag>,

              },

              {

                title: '印章预览',

                width: 90,

                render: (_, r) => r.seal_img ? (

                  <img

                    src={companySealImgUrl(r.id)}

                    style={{ width: 64, height: 64, objectFit: 'contain', border: '1px solid #eee', cursor: 'pointer' }}

                    onClick={() => {

                      const w = window.open('', '_blank');

                      if (w) { w.document.write(`<img src='${companySealImgUrl(r.id)}' style='max-width:90vw'/>`); w.document.close(); }

                    }}

                  />

                ) : <Tag>—</Tag>,

              },

              {

                title: '操作',

                width: 120,

                render: (_, r) => (

                  <Space size={0}>

                    <Popconfirm

                      title={r.is_active === 0 ? '确认启用？' : '确认停用？'}

                      onConfirm={() => {

                        axios.patch(`/api/asign/stamp/toggle/${r.id}`, {}, { headers: h() })

                          .then(() => { message.success('操作成功'); fetchStampAdminList(); })

                          .catch(() => message.error('操作失败'));

                      }}

                    >

                      <Button type="link" size="small" style={{ color: r.is_active === 0 ? '#52c41a' : '#faad14' }}>

                        {r.is_active === 0 ? '启用' : '停用'}

                      </Button>

                    </Popconfirm>

                  </Space>

                ),

              },

            ]}

          />

        </Card>

      )}

      {/* ========== 我的入库章（非管理员） ========== */}

      {!isAdmin && (

        <Card

          title="我的入库章"

          style={{ marginBottom: 16 }}

          extra={<Button size="small" onClick={() => fetchMySealList()}>刷新</Button>}

        >

          <Table

            dataSource={mySealList}

            rowKey="id"

            size="small"

            loading={mySealLoading}

            pagination={false}

            columns={[

              {

                title: '印章编号',

                dataIndex: 'asign_seal_no',

                width: 200,

                ellipsis: true,

                render: v => v ? <Tag color="purple" style={{fontFamily:'monospace',fontSize:11}}>{v}</Tag> : <Tag color="default">—</Tag>,

              },

              {

                title: '印章名称',

                dataIndex: 'seal_title',

                width: 200,

                ellipsis: true,

                render: v => v || '—',

              },

              {

                title: '印章状态',

                width: 80,

                render: (_, r) => r.seal_img

                  ? <Tag color="green">已制作</Tag>

                  : <Tag color="default">未制作</Tag>,

              },

              {

                title: '爱签同步',

                width: 80,

                render: v => v

                  ? <Tag color="blue">已同步</Tag>

                  : <Tag color="default">未同步</Tag>,

              },

              {

                title: '印章预览',

                width: 90,

                render: (_, r) => r.seal_img ? (

                  <img

                    src={companySealImgUrl(r.id)}

                    style={{ width: 64, height: 64, objectFit: 'contain', border: '1px solid #eee', cursor: 'pointer' }}

                    onClick={() => {

                      const w = window.open('', '_blank');

                      if (w) { w.document.write(`<img src='${companySealImgUrl(r.id)}' style='max-width:90vw'/>`); w.document.close(); }

                    }}

                  />

                ) : <Tag>—</Tag>,

              },

              {

                title: '操作',

                width: 80,

                render: (_, r) => (

                  <Button type="link" size="small" onClick={() => openCompanySealModal(r)}>修改</Button>

                ),

              },

            ]}

          />

        </Card>

      )}

      {/* ========== 委托书管理 ========== */}

      <Card id="section-letter" title="委托书管理" style={{ marginBottom: 16 }}

        extra={

          <span>

            <a href="/api/download/新签收委托书模板.docx" target="_blank" style={{ fontSize: 13, marginRight: 12 }}>📥 委托书样例下载</a>

            <Button type="primary" icon={<PlusOutlined />} onClick={() => setAuthModalOpen(true)}>

              上传委托书

            </Button>

          </span>

        }>

        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>

          <Input placeholder="客户名称" value={letterFilter.name}

            onChange={e => setLetterFilter(f => ({ ...f, name: e.target.value }))}

            style={{ width: 150 }} allowClear />

          <Input placeholder="客户编码" value={letterFilter.code}

            onChange={e => setLetterFilter(f => ({ ...f, code: e.target.value }))}

            style={{ width: 130 }} allowClear />

          <Select placeholder="审核状态" value={letterFilter.status || undefined}

            onChange={v => setLetterFilter(f => ({ ...f, status: v || '' }))}

            style={{ width: 120 }} allowClear

            options={[

              { value: 'pending', label: '待上传' },

              { value: 'submitted', label: '待审核' },

              { value: 'passed', label: '已通过' },

              { value: 'rejected', label: '已驳回' },

            ]}

          />

        </div>

        <Table dataSource={authLetters.filter(l =>


          (!letterFilter.name || (l.ccusname || '').includes(letterFilter.name)) &&

          (!letterFilter.code || (l.cCusCode || '').includes(letterFilter.code)) &&

          (!letterFilter.status || l.auth_status === letterFilter.status)

        )} rowKey="id" size="small" pagination={false} scroll={{ x: 'max-content' }} columns={[

          { title: '客户名称', dataIndex: 'ccusname', width: 160, ellipsis: true },

          { title: '客户编码', dataIndex: 'cCusCode', width: 120 },

          { title: '类型', dataIndex: 'is_enterprise', width: 80,

            render: v => v ? <Tag color="blue">企业认证</Tag> : <Tag>委托书</Tag> },

          { title: '审核状态', dataIndex: 'auth_status', width: 80,

            render: v => <Tag color={statusMap[v]?.color}>{statusMap[v]?.text}</Tag> },

          { title: '上传时间', dataIndex: 'created_at', width: 160,

            render: v => v ? new Date(v).toLocaleString('zh-CN') : '-' },

          // 爱签同步列已移除

          { title: '启用', dataIndex: 'is_active', width: 60,

            render: v => v === 0 ? <Tag color="red">已停用</Tag> : <Tag color="green">已启用</Tag> },

          { title: '委托书', width: 80, render: showPreview },

          { title: '操作', width: isAdmin ? 220 : 300,

            render: (_, r) => {

              if (r.is_enterprise) return isAdmin ? <Tag>企业认证</Tag> : null;

              const canReview = isAdmin && r.auth_status === 'submitted';

              const isRejected = r.auth_status === 'rejected';

              return (

                <Space size={0} direction="vertical" align="start">

                  {/* 管理员：审核按钮 */}

                  {canReview ? (

                    <>

                      <Button type="link" size="small" icon={<CheckCircleOutlined />} style={{ color: '#52c41a' }}

                        onClick={() => setReviewModal({ open: true, letter: r, action: 'passed' })}>通过</Button>

                      <Button type="link" size="small" danger icon={<CloseCircleOutlined />}

                        onClick={() => setReviewModal({ open: true, letter: r, action: 'rejected' })}>驳回</Button>

                    </>

                  ) : null}

                  {/* 非管理员：已驳回时仅显示状态提示（驳回原因在"提交审核"卡片区域展示） */}

                  {!isAdmin && isRejected && (

                    <Tag color="error">已驳回</Tag>

                  )}

                  {/* 启用/停用 按钮（管理员） */}

                  {isAdmin && (

                    <Popconfirm title={r.is_active === 0 ? '确认启用？' : '确认停用？'} onConfirm={() => toggleLetter(r.id, r.is_active)}>

                      <Button type="link" size="small"

                        style={{ color: r.is_active === 0 ? '#52c41a' : '#faad14' }}>

                        {r.is_active === 0 ? '启用' : '停用'}

                      </Button>

                    </Popconfirm>

                  )}

                  {isAdmin && (

                    <Button type="link" size="small" danger onClick={() => deleteLetter(r.id)}>删除</Button>

                  )}

                </Space>

              );

            },

          },

        ]} locale={{ emptyText: '暂无委托书，请点击右上角"上传委托书"提交' }} scroll={{ x: 'max-content' }} />

      </Card>

      {/* ========== 签收人员 ========== */}

      <Card id="section-signers" title={<span>签收人员<span style={{ color: "#ff4d4f", fontSize: 14, fontWeight: "bold" }}>（调整签收人员信息，请务必与委托书授权信息保持一致）</span></span>}

        extra={

          <Button type="primary" icon={<PlusOutlined />} onClick={() => setSignerModalOpen(true)}>

            添加签收人

          </Button>

        }>

        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>

          <Input placeholder="爱签账号(客户编码)" value={signerFilter.code}

            onChange={e => setSignerFilter(f => ({ ...f, code: e.target.value }))}

            style={{ width: 180 }} allowClear />

          <Select placeholder="操作状态" value={signerFilter.opStatus || undefined}

            onChange={v => setSignerFilter(f => ({ ...f, opStatus: v || '' }))}

            style={{ width: 130 }} allowClear

            options={[

              { value: 'unauth', label: '未认证' },

              { value: 'authed', label: '已认证' },

              { value: 'synced', label: '已同步' },

            ]}

          />

        </div>

        <Table dataSource={signers.filter(s =>

          (!signerFilter.code || (s.asign_account || '').includes(signerFilter.code)) &&

          (!signerFilter.opStatus ||

            (signerFilter.opStatus === 'unauth' && !s.asign_account) ||

            (signerFilter.opStatus === 'authed' && s.asign_account && !s.asign_seal_no) ||

            (signerFilter.opStatus === 'synced' && s.asign_seal_no))

        )} rowKey="id" size="small" pagination={false} scroll={{ x: 'max-content' }} columns={[

          { title: '姓名', dataIndex: 'signer_name', width: 80 },

          { title: '手机', dataIndex: 'phone', width: 120 },

          { title: '签收密码', dataIndex: 'has_pwd', width: 90,

            render: v => v ? <Tag color="green">已设置</Tag> : <Tag color="orange">未设置</Tag> },

          { title: '爱签账号', dataIndex: 'asign_account', width: 140, ellipsis: true,

            render: v => v || <Tag color="default">未认证</Tag> },

          { title: '签名图片', dataIndex: 'seal_img', width: 80,

            render: (v, r) => v ? <Button type="link" size="small" icon={<EyeOutlined />}

              onClick={() => previewImg(v)}>查看</Button> : <span style={{ color: '#ccc' }}>无</span> },

          { title: '启用状态', dataIndex: 'is_active', width: 70,

            render: (v, r) => (

              <Popconfirm title={v ? '确认停用该签收人？' : '确认启用该签收人？'}

                onConfirm={() => toggleSigner(r.id, v)}>

                <Button size="small"

                  style={v ? { borderColor: '#52c41a', color: '#52c41a' } : { borderColor: '#ff4d4f', color: '#ff4d4f' }}>

                  {v ? '启用' : '停用'}

                </Button>

              </Popconfirm>

            ) },

          { title: '爱签印章', width: 120,

            render: (_, r) => r.asign_seal_no
              ? <Tag color="green">已同步</Tag>

              : <Tag color="default">未同步</Tag> },

          { title: '操作', key: 'actions', width: 280,

            render: (_, r) => {

              const hasAccount = !!r.asign_account;

              const hasSig = !!r.seal_img;

              const hasSeal = !!r.asign_seal_no;

              // 存在委托书（任意状态）即可进行个人认证

              const canAuth = authLetters.some(l => !l.is_enterprise && l.auth_status !== 'rejected');

              return (

                <Space size={4} wrap>

                  {!hasAccount ? (

                    canAuth ? (
                      <>
                      <Button size="small" type="primary"

                        onClick={() => {

                          if (!r.is_active) { message.warning('签收人员已停用，启用后再认证'); return; }

                          startSignerAuth(r, 'web');

                        }}>

                        去认证（网页）
                      </Button>
                        <Button size="small" onClick={() => confirmAuthComplete(r.id)}>认证完成</Button>
                      </>
                    ) : (

                      <Tag color="default">待审核</Tag>

                    )

                  ) : (

                    <>

                      {hasAccount && (

                        <Button size="small" type="primary" ghost

                          onClick={() => setPersonalSealModal({ open: true, signer: r })}>

                          个人印章制作

                        </Button>

                      )}

                    </>

                  )}

                  {!hasAccount && (
                    <Button size="small" onClick={() => openModifySigner(r)}>修改</Button>
                  )}
                  <Button size="small" icon={<QrcodeOutlined />} onClick={() => setPwdQrModal({ open: true, signer: r })}>密码二维码</Button>
                  {isAdmin && (

                    <Button size="small" danger onClick={() => deleteSigner(r.id, r.signer_name)}>删除</Button>

                  )}

                </Space>

              );

            },

          },

        ]} locale={{ emptyText: '暂无签收人，请先上传委托书后添加' }} scroll={{ x: 'max-content' }} />

      </Card>

      

      {/* ========== 修改签收人 Modal ========== */}
      <Modal title="修改签收人信息" open={modifySignerModalOpen}
        onCancel={() => setModifySignerModalOpen(false)}
        onOk={handleModifySignerSubmit} okText="提交修改"
        destroyOnClose>
        <Form form={modifySignerForm} layout="vertical">
          <Form.Item name="signer_name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
            <Input placeholder="签收人姓名" />
          </Form.Item>
          <Form.Item name="phone" label="手机号" rules={[{ required: true, message: '请输入手机号' }]}>
            <Input placeholder="手机号" maxLength={11} />
          </Form.Item>
          <Form.Item name="id_card" label="身份证号" rules={[{ required: true, message: '请输入身份证号' }, { pattern: /^[1-9]\d{5}(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}[\dXx]$/, message: '身份证号格式不正确' }]}>
            <Input placeholder="身份证号" maxLength={18} />
          </Form.Item>
        </Form>
      </Modal>
{/* ========== 签收人认证 Modal ========== */}

      <Modal title={authSignerModal.step === 'choose' ? '选择认证方式' : '输入验证码'}

        open={authSignerModal.open}

        onCancel={() => setAuthSignerModal({ open: false, signer: null, step: 'choose', serialNo: '' })}

        footer={authSignerModal.step === 'choose' ? null : null} width={400}>

        {authSignerModal.step === 'choose' ? (

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', padding: '16px 0' }}>

            <Button type="primary" size="large" onClick={() => { if (!authSignerModal.signer?.is_active) { message.warning('签收人员已停用，启用后再认证'); return; } startSignerAuth(authSignerModal.signer, 'mobile3'); }}>

              手机认证（短信验证码）

            </Button>

            <Button size="large" onClick={() => { if (!authSignerModal.signer?.is_active) { message.warning('签收人员已停用，启用后再认证'); return; } startSignerAuth(authSignerModal.signer, 'web'); }}>

              网页认证

            </Button>

          </div>

        ) : (

          <>

            <p style={{ color: '#888' }}>验证码已发送至 {authSignerModal.signer?.phone}</p>

            <Form form={verifyForm} layout="vertical">

              <Form.Item name="serial_no" hidden initialValue={authSignerModal.serialNo}>

                <Input />

              </Form.Item>

              <Form.Item name="code" label="短信验证码" rules={[{ required: true, message: '请输入验证码' }]}>

                <Input placeholder="6位验证码" maxLength={6} />

              </Form.Item>

            </Form>

            <div style={{ textAlign: 'right' }}>

              <Button onClick={() => setAuthSignerModal({ open: false, signer: null, step: 'choose', serialNo: '' })} style={{ marginRight: 8 }}>取消</Button>

              <Button type="primary" onClick={verifySignerAuth}>确认</Button>

            </div>

          </>

        )}

      </Modal>

      {/* ========== 委托书上传 Modal ========== */}

      <Modal title="上传委托书" open={authModalOpen} onCancel={() => setAuthModalOpen(false)}

        onOk={handleAuthSubmit} confirmLoading={submitting} okText="提交" width={480}

        destroyOnClose>

        <div style={{ marginBottom: 16, padding: '8px 12px', background: '#fff7e6', borderRadius: 6, border: '1px solid #ffd591', fontSize: 13 }}>

          ⚠️ 请填写<strong>被授权签收人</strong>的真实信息，审核通过后将启用该客户的签收权限。<br/>

          如无委托书模板，请先 <a href="/api/download/新签收委托书模板.docx" target="_blank" style={{ color: '#1890ff' }}>📥 下载委托书模板</a>，打印填写盖章后拍照上传。

        </div>

        <Form form={authForm} layout="vertical">

          <Form.Item name="file" label="委托书PDF文件" rules={[{ required: true, message: '请上传委托书PDF文件' }]}

            valuePropName="file">

            <Upload maxCount={1} beforeUpload={(file) => {

              const isPDF = file.type === 'application/pdf' || file.name.endsWith('.pdf');

              if (!isPDF) { message.error('委托书只能上传PDF文件！'); return Upload.LIST_IGNORE; }

              return false;

            }} accept=".pdf">

              <Button icon={<UploadOutlined />}>选择PDF文件</Button>

            </Upload>

          </Form.Item>

        </Form>

      </Modal>

      {/* ========== 签收人添加 Modal ========== */}

      <Modal title="添加签收人" open={signerModalOpen} onCancel={() => setSignerModalOpen(false)}

        onOk={handleSignerSubmit} okText="添加" destroyOnClose key={signerModalOpen ? 'signer-open' : 'signer-closed'}>

        <Form form={signerForm} layout="vertical">

          <Form.Item name="signer_name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>

            <Input placeholder="签收人姓名" />

          </Form.Item>

          <Form.Item name="phone" label="手机号" rules={[{ required: true, message: '请输入手机号' }]}>

            <Input placeholder="手机号" maxLength={11} />

          </Form.Item>

          <Form.Item name="id_card" label="身份证号" rules={[{ required: true, message: '请输入身份证号' }, { pattern: /^[1-9]\d{5}(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}[\dXx]$/, message: '身份证号格式不正确' }]}>

            <Input placeholder="身份证号" maxLength={18} />

          </Form.Item>

          <Form.Item name="sign_password" label="签收密码" rules={[{ min: 6, message: '签收密码至少6位' }]}
            extra="签收密码仅用于手机端签收，一个签收人一个密码">

            <Input.Password placeholder="请设置签收密码（至少6位）" maxLength={20} autoComplete="new-password" />

          </Form.Item>

          <Form.Item name="confirm_password" label="确认密码"
            dependencies={['sign_password']}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!getFieldValue('sign_password') || getFieldValue('sign_password') === value) return Promise.resolve();
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}>

            <Input.Password placeholder="请再次输入签收密码" maxLength={20} autoComplete="new-password" />

          </Form.Item>

        </Form>

      </Modal>

      {/* ========== 签收密码二维码 Modal ========== */}

      <Modal title={`签收密码设置二维码 - ${pwdQrModal.signer?.signer_name || ''}`} open={pwdQrModal.open}

        onCancel={() => setPwdQrModal({ open: false, signer: null })}

        footer={null} width={360} centered>

        <div style={{ textAlign: 'center', padding: '16px 0' }}>

          {pwdQrModal.signer && (
            <img src={`${API}/customer/signer/${pwdQrModal.signer.id}/password-qrcode`}
              alt="签收密码设置二维码" style={{ width: 220, height: 220 }} />
          )}

          <div style={{ marginTop: 12, color: '#666', fontSize: 13 }}>

            签收人：{pwdQrModal.signer?.signer_name || ''}

          </div>

          <div style={{ marginTop: 4, color: '#999', fontSize: 12 }}>

            请将二维码发给该签收人，用手机扫码设置签收密码

          </div>

          <Button type="primary" icon={<DownloadOutlined />} style={{ marginTop: 12 }}

            onClick={() => {

              const s = pwdQrModal.signer;

              if (!s) return;

              const a = document.createElement('a');

              a.href = `${API}/customer/signer/${s.id}/password-qrcode`;

              a.download = `签收密码二维码_${s.signer_name}.png`;

              a.click();

            }}>下载二维码</Button>

        </div>

      </Modal>

      {/* ========== 管理员审核确认 Modal ========== */}

      <Modal title={reviewModal.action === 'passed' ? '确认通过' : '确认驳回'}

        open={reviewModal.open}

        onCancel={() => setReviewModal({ open: false, letter: null, action: '' })}

        onOk={handleReview} okText="确认" cancelText="取消"

        okButtonProps={{ danger: reviewModal.action === 'rejected' }}>

        <p>

          {reviewModal.action === 'passed'

            ? `确认通过「${reviewModal.letter?.ccusname}」的委托书？通过后将启用该客户的签收权限（需已有签收人）。`

            : `确认驳回「${reviewModal.letter?.ccusname}」的委托书？`}

        </p>

      </Modal>

      {/* ========== 企业认证 Modal ========== */}

      <Modal title="企业认证" open={certModalOpen} onCancel={() => setCertModalOpen(false)}

        onOk={handleCompanyCert} confirmLoading={certLoading} okText="获取认证链接" width={480}>

        <Form form={certForm} layout="vertical">

          <Form.Item name="company_name" label="企业名称" rules={[{ required: true }]}>

            <Input placeholder="企业营业执照全称" disabled />

          </Form.Item>

          <Form.Item name="credit_code" label="统一社会信用代码" rules={[{ required: true, message: '请输入信用代码' }]}>

            <Input placeholder="18位统一社会信用代码" maxLength={18} />

          </Form.Item>

        </Form>

      </Modal>

      {/* ========== 认证教程 Modal ========== */}

      <Modal title="企业认证教程" open={tutorialOpen} onCancel={() => setTutorialOpen(false)}

        footer={<Button onClick={() => setTutorialOpen(false)}>关闭</Button>} width={700}>

          <h4>一、准备材料</h4>

          <ol>

            <li>企业营业执照（知晓统一社会信用代码）</li>

            <li>法人身份证信息</li>

            <li>操作人手机（需能接收短信验证码）</li>

          </ol>

          <h4>二、操作步骤</h4>

          <ol>

            <li>点击页面上的 <b>「企业认证」</b> 按钮</li>

            <li>填写 <b>企业名称</b> 和 <b>统一社会信用代码</b></li>

            <li>点击「获取认证链接」，系统会打开爱签CA机构认证页面</li>

            <li>在认证页面中选择认证方式（推荐<strong>法定代表人认证</strong>）</li>

            <li>按页面提示完成法人身份验证</li>

            <li>设置<strong>签署密码</strong>（后续到货签收不会使用）</li>

            <li>认证成功后，确认认证完成后关闭结果页即可</li>

          </ol>

          <h4>三、可选的认证方式</h4>

          <table style={{ borderCollapse: 'collapse', width: '100%' }}>

            <thead><tr style={{ background: '#f5f5f5' }}>

              <th style={{ border: '1px solid #ddd', padding: 4 }}>方式</th>

              <th style={{ border: '1px solid #ddd', padding: 4 }}>说明</th>

            </tr></thead>

            <tbody>

              <tr><td style={{ border: '1px solid #ddd', padding: 4 }}>法定代表人认证</td><td style={{ border: '1px solid #ddd', padding: 4 }}>法人本人操作，快速认证</td></tr>

              <tr><td style={{ border: '1px solid #ddd', padding: 4 }}>对公打款认证</td><td style={{ border: '1px solid #ddd', padding: 4 }}>平台向企业对公账户打款验证</td></tr>

              <tr><td style={{ border: '1px solid #ddd', padding: 4 }}>法人授权书认证</td><td style={{ border: '1px solid #ddd', padding: 4 }}>非法人操作，上传授权书</td></tr>

            </tbody>

          </table>

          <h4>四、注意事项</h4>

          <ul>
            <li>认证信息需与工商登记信息<strong>完全一致</strong></li>

            <li>签署密码需妥善保管，签收时要用</li>

            <li>认证通过后请联系管理员审核</li>

          </ul>

      </Modal>

      {/* ========== 认证流水号查询 Modal ========== */}

      <Modal title="查询认证结果" open={serialNoModal} onCancel={() => setSerialNoModal(false)}

        onOk={async () => {

          try {

            const vals = await serialNoForm.validateFields();

            setCertLoading(true);

            const r = await axios.post(API + '/customer/company-cert-done', {

              serial_no: vals.serial_no,

              cCusCode: isAdmin ? adminCustomer : undefined,

            }, { headers: h() });

            if (r.data.success && r.data.passed) {

              message.success(r.data.message || '认证通过！');

              setSerialNoModal(false);

              serialNoForm.resetFields();

              fetchAll();

            } else {

              message.warning(r.data.message || '查询失败');

            }

          } catch (e) {

            if (e.errorFields) return;

            message.error(e.response?.data?.detail || '查询失败');

          }

          setCertLoading(false);

        }} confirmLoading={certLoading} okText="查询">

        <p style={{ color: '#888', marginBottom: 12 }}>请输入爱签认证流水号查询认证结果</p>

        <Form form={serialNoForm} layout="vertical">

          <Form.Item name="serial_no" label="认证流水号" rules={[{ required: true, message: '请输入认证流水号' }]}>

            <Input placeholder="如：CA35020230424104548033790" />

          </Form.Item>

        </Form>

      </Modal>

      {/* ========== 签收人确认 Modal ========== */}
      <SignerConfirmModal
        open={signerConfirmOpen}
        signers={pendingSigners}
        onCancel={() => setSignerConfirmOpen(false)}
        onOk={batchSaveSigners}
        onFieldChange={handleSignerFieldChange}
        onAddRow={addSignerRow}
        onRemoveRow={removeSignerRow}
        onSignatureFile={handleSignatureFile}
      />

      {/* 印章制作弹窗 */}

      <Modal

        title="制作印章"

        open={stampModal}

        onCancel={() => { setStampModal(false); setEditingSealId(null); }}

        footer={null}

        width={700}

      >

        <div style={{ display: 'flex', gap: 16 }}>

          <div style={{ flex: 1 }}>

            <Form layout="vertical">

              <Form.Item label="印章样式">

                <Select value={stampFields.style} onChange={updateStampStyle}>

                  {COMPANY_STYLES.map(s => (

                    <Select.Option key={s.value} value={s.value}>{s.label}</Select.Option>

                  ))}

                </Select>

              </Form.Item>

              <Form.Item label="印章名称">

                <Input value={stampFields.stampText}

                  onChange={e => setStampFields(f => ({ ...f, stampText: e.target.value }))} />

              </Form.Item>

              <Form.Item label="顶部文字（公司名称）">

                <Input value={stampFields.topText} disabled />

              </Form.Item>

              <Form.Item label="顶部文字字号">

                <Slider min={0} max={36} value={stampFields.topFontSize}

                  onChange={v => setStampFields(f => ({ ...f, topFontSize: v }))} />

                <div style={{ fontSize: 12, color: '#999' }}>0=自动</div>

              </Form.Item>

              <Form.Item label="中心文字">
                <Select value={stampFields.centerText}
                  onChange={v => setStampFields(f => ({ ...f, centerText: v }))}
                  options={[
                    { value: '入库专用章', label: '入库专用章' },
                    { value: '收货专用章', label: '收货专用章' },
                  ]} />

              </Form.Item>

              <Form.Item label="中心文字字号">

                <Slider min={0} max={36} value={stampFields.centerFontSize}

                  onChange={v => setStampFields(f => ({ ...f, centerFontSize: v }))} />

                <div style={{ fontSize: 12, color: '#999' }}>0=自动</div>

              </Form.Item>

              <Form.Item label="显示五角星">

                <Switch checked={stampFields.showStar}

                  onChange={v => setStampFields(f => ({ ...f, showStar: v }))} />

              </Form.Item>

              <Button type="primary" block loading={stampLoading} onClick={makeCompanySeal}>

                {editingSealId ? '确认修改' : '提交制作'}

              </Button>

            </Form>

          </div>

          <div style={{ width: `min(${stampPreviewSize.w}px, 30vw)`, flexShrink: 0 }}>

            <canvas ref={el => { stampCanvasRef.current = el; if (el) { el.width = stampPreviewSize.w; el.height = stampPreviewSize.h; sealCanvas(el, stampFields); } }} style={{ width: '100%', height: 'auto' }} />

          </div>

        </div>

        {stampList.length > 0 && (

          <div style={{ marginTop: 16 }}>

            <div style={{ fontWeight: 500, marginBottom: 8 }}>已有印章：</div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>

              {stampList.map(s => (

                <img key={s.id} src={companySealImgUrl(s.id)} style={{ width: 80, height: 80, border: '1px solid #ddd' }} />

              ))}

            </div>

          </div>

        )}

      </Modal>

      {/* ========== 个人印章制作（新爱签V2参数化弹窗） ========== */}

      <PersonalSealModal

        open={personalSealModal.open}

        signer={personalSealModal.signer}

        onClose={() => setPersonalSealModal({ open: false, signer: null })}

        onSuccess={fetchAll}

      />

    </div>

  );

}

